import { Logger } from '~~/helpers/logger';

import type { Customer, CustomQuery, UseUserErrors, UseUserLoginParams, UseUserRegisterParams } from '~~/types/commercetools';
import { COHORT_TIER_KEY } from '~~/constants/discounts';
import { useUser } from '~/utils/ct';
import { centAmountToNormalPrice } from '~~/helpers/cart/getTotalPricesModule';

type ErrorState = Partial<Record<keyof UseUserErrors, null | Error>>;

export default function () {

  const { event: ciaEvent } = useCia();
  const {
    load,
    user,
    logout,
    login,
    updateUser,
    register,
    changePassword,
    isAuthenticated,
    loading,
    error: oldError,
  } = useUser();
  const { setCustomerIdCookie } = useCookies();
  const { languageAndCountry } = useLocalization();
  const { updateCustomerEmail, clearIfOtherStoreCart: clearCartIfOtherStoreCart } = useCartExtended();
  const { setEmail, setUserId } = useCiaState();
  const { reset: resetLoyaltyPoints } = useLoyaltyPoints();
  const { reset: resetLoyaltyPointsHistory } = useLoyaltyPointsHistory();

  // Computed property to ensure the oldError type matches ErrorState
  const oldErrorWithCorrectType = computed(() => oldError.value as ErrorState);

  // Initializing error state with the correct type as @vue-storefront type wasn't correct
  const error = ref<ErrorState>(oldErrorWithCorrectType.value);

  const updateCustomer = async (customer: Partial<Customer>, sentEventToCia: boolean) => {

    if (customer) {

      ciaCustomerUpdate(customer, sentEventToCia);
      await updateCustomerEmail(customer.email, 'updateCustomer');

    }

  };

  const ciaCustomerUpdate = (customer: Partial<Customer>, sentEventToCia: boolean) => {

    try {

      setUserId(customer.id || '');

    } catch (error) {

      Logger.error(`cia|setUserId error: ${error}`);

    }

    /** running an event for registering in CIA that user has changed data */
    if (sentEventToCia) {

      try {

        ciaEvent.customerUpdate(customer.id);

      } catch (error) {

        Logger.error(`cia|customerUpdate error: ${error}`);

      }

    }

  };

  async function newLoad () {

    await load();

    if (user.value) {

      await updateCustomer(user.value, false);

    }

  }

  async function newLogin (params: { userData: UseUserLoginParams }) {

    await login({ user: params.userData });
    await clearCartIfOtherStoreCart();

    if (user.value) {

      await updateCustomer(user.value, true);
      setCustomerIdCookie(user.value?.id);

    }

  }

  async function newLogout () {

    setCustomerIdCookie('');
    await logout();
    resetLoyaltyPoints();
    resetLoyaltyPointsHistory();

    try {

      setEmail('');
      setUserId('');

    } catch (error) {

      Logger.error(`cia|Error while registering logout event: ${error}`);

    }

  }

  async function newUpdateUser (params: { updatedUserData: any, customQuery?: CustomQuery }) {

    await updateUser({ providedUser: params.updatedUserData, customQuery: params.customQuery });

    if (user.value) {

      await updateCustomer(user.value, true);

    }

  }

  async function newRegister (params: { userData: UseUserRegisterParams }) {

    const custom = params.userData.customerType
      ? {
        type: {
          key: 'customerCustomFields',
          typeId: 'type',
        },
        fields: [
          {
            name: 'customerType',
            value: JSON.stringify(params.userData.customerType.toLowerCase()),
          },
        ],
      }
      : undefined;
    const userData = {
      ...params.userData,
      locale: languageAndCountry.value,
      custom,
    };
    await register({
      providedUser: userData,
    });

    if (user.value) {

      await updateCustomer(user.value, true);
      setCustomerIdCookie(user.value?.id);

    }

  }

  function resetError() {

    const errorKeys = Object.keys(error.value);
    errorKeys.forEach((errorKey) => {

      if (isErrorKeyValid(errorKey, error.value)) {

        error.value[errorKey] = null;

      }

    });

  }

  function isErrorKeyValid(errorKey: string, errorObject: ErrorState): errorKey is keyof ErrorState {

    return errorKey in errorObject;

  }

  const isLoggedIn = computed<boolean>(() => {

    return !!user.value;

  });

  const { shouldUseMockData, mockedPartnersData } = useExpondoPartnersMock();

  const customerTierKey = computed<string | null>(() => {

    if (!isLoggedIn.value) {

      return COHORT_TIER_KEY.NOT_LOGGED_IN;

    }

    if (loading.value) {

      return null;

    }

    const mockedCustomerGroupKey = shouldUseMockData.value ? mockedPartnersData.value?.customerGroupKey : null;

    return mockedCustomerGroupKey ?? user.value?.customerGroupAssignments?.[0]?.customerGroup?.key ?? null;

  });

  const customerOrdersSum = computed<number>(() => {

    if (!isLoggedIn.value) {

      return 0;

    }

    if (loading.value) {

      return null;

    }

    let ordersSum = user.value?.custom?.customFieldsRaw?.find(
      (field) => field.name === 'OrdersSum')?.value?.centAmount ?? 0;

    if (ordersSum > 0) {

      ordersSum = centAmountToNormalPrice(ordersSum);

    }

    return ordersSum;

  });

  watch(oldError, (newVal) => {

    error.value = newVal;

  }, { deep: true });

  onMounted(() => {

    resetError();

  });

  return {
    // TODO - revert syntax once fix is pushed for type incoherence between composables and getters
    //  https://expondo.atlassian.net/browse/INSP-988
    user: user as Ref<Customer>,
    load: newLoad,
    logout: newLogout,
    updateUser: newUpdateUser,
    register: newRegister,
    login: newLogin,
    isAuthenticated,
    changePassword,
    loading,
    error,
    resetError,
    customerTierKey,
    customerOrdersSum,
    isLoggedIn,
  };

}
